"use client";

import { c as _c } from "react/compiler-runtime";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import Link from "next/link";
import type { ReactNode } from "react";
import { MetaDot } from "./meta-dot";
import { PageHeader } from "./page-header";
interface PageLayoutProps {
  /**
   * Main content to be displayed in the page
   */
  children: ReactNode;

  /**
   * Layout variant
   * - "full-width": Default layout with full-width content
   * - "sidebar": Layout with main content and sidebar (responsive: stacks on mobile)
   * - "bare": No outer card wrap, no footer chrome — children own the full page area
   *   (use when the page composes its own cards, e.g. multi-card grid layouts)
   */
  variant?: "full-width" | "sidebar" | "bare";

  /**
   * Sidebar content (only used when variant="sidebar")
   */
  sidebar?: ReactNode;

  /**
   * Optional header configuration for PageHeader component
   */
  header?: {
    /**
     * Content to show in the header (e.g., back button, breadcrumbs)
     */
    children?: ReactNode;
    /**
     * Search callback
     */
    onSearch?: (query: string) => void;
    /**
     * Search placeholder text
     */
    searchPlaceholder?: string;
    /**
     * Whether to show search input
     */
    showSearch?: boolean;
    /**
     * Whether clicking search opens command menu (default: true)
     * Set to false for a regular search input
     */
    useCommandMenu?: boolean;
    /**
     * Filter elements to show on the right side
     */
    filters?: ReactNode;
    /**
     * Data-guide attribute for the search input
     */
    searchDataGuide?: string;
  };

  /**
   * Custom background class (defaults to transparent so the route shell owns the page backdrop)
   */
  backgroundClassName?: string;

  /**
   * Custom card class to override default card styling
   */
  cardClassName?: string;

  /**
   * Custom padding class for card content (defaults to p-6)
   */
  contentPadding?: string;

  /**
   * Optional sticky rail rendered outside the page shell
   */
  rail?: ReactNode;

  /**
   * Custom classes for the sticky rail wrapper
   */
  railClassName?: string;

  /**
   * Custom classes for the outer page frame
   */
  frameClassName?: string;
}

/**
 * Shared page layout component for v2 routes
 *
 * @example Full-width layout
 * ```tsx
 * <PageLayout>
 *   <h1>My Content</h1>
 * </PageLayout>
 * ```
 *
 * @example Sidebar layout with header
 * ```tsx
 * <PageLayout
 *   variant="sidebar"
 *   header={{
 *     children: <BackButton />,
 *     onSearch: handleSearch,
 *     searchPlaceholder: "Search...",
 *   }}
 *   sidebar={<SidebarContent />}
 * >
 *   <MainContent />
 * </PageLayout>
 * ```
 */
export function PageLayout(t0) {
  const $ = _c(60);
  const {
    children,
    variant: t1,
    sidebar,
    header,
    backgroundClassName: t2,
    cardClassName,
    contentPadding: t3,
    rail,
    railClassName,
    frameClassName
  } = t0;
  const variant = t1 === undefined ? "full-width" : t1;
  const backgroundClassName = t2 === undefined ? "bg-transparent" : t2;
  const contentPadding = t3 === undefined ? "p-6 xl:p-8 xl:px-12 h-full flex-1" : t3;
  const finalCardClassName = cardClassName || "ds-shell min-h-[calc(100dvh-20px)] flex flex-col dark:border-ds-neutral-800 dark:bg-zinc-950 dark:shadow-none";
  let t4;
  if ($[0] !== railClassName) {
    t4 = cn("sticky top-2 hidden h-[calc(100dvh-16px)] shrink-0 self-start lg:flex lg:items-start lg:px-[6px]", railClassName);
    $[0] = railClassName;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const finalRailClassName = t4;
  if (variant === "bare") {
    let t5;
    if ($[2] !== frameClassName) {
      t5 = cn("m-2 mr-11 mt-3 min-w-0", frameClassName);
      $[2] = frameClassName;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== children || $[5] !== t5) {
      t6 = <div className={t5}>{children}</div>;
      $[4] = children;
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    const bareContent = t6;
    let t7;
    if ($[7] !== bareContent || $[8] !== finalRailClassName || $[9] !== rail) {
      t7 = rail ? <div className="grid items-start gap-0 lg:grid-cols-[minmax(0,1fr)_auto]"><div className="min-w-0 flex-1">{bareContent}</div><div className={finalRailClassName}>{rail}</div></div> : bareContent;
      $[7] = bareContent;
      $[8] = finalRailClassName;
      $[9] = rail;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== backgroundClassName || $[12] !== t7) {
      t8 = <div className={backgroundClassName}>{t7}</div>;
      $[11] = backgroundClassName;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (variant === "sidebar") {
    let t5;
    if ($[14] !== header) {
      t5 = header && <PageHeader {...header} />;
      $[14] = header;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== children || $[17] !== contentPadding) {
      t6 = <div className={contentPadding}>{children}</div>;
      $[16] = children;
      $[17] = contentPadding;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="border-ds-border/50 flex justify-end border-t px-6 py-3"><StatusBadge /></div>;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== finalCardClassName || $[21] !== t5 || $[22] !== t6) {
      t8 = <div className="flex-1 sm:w-full lg:w-2/3 xl:w-3/5"><div className={finalCardClassName}>{t5}{t6}{t7}</div></div>;
      $[20] = finalCardClassName;
      $[21] = t5;
      $[22] = t6;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== sidebar) {
      t9 = sidebar && <div className="max-w-[360px] px-2 pt-4 sm:w-full lg:w-1/3 xl:w-2/5">{sidebar}</div>;
      $[24] = sidebar;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t8 || $[27] !== t9) {
      t10 = <div className="flex gap-4 p-2 max-lg:flex-col">{t8}{t9}</div>;
      $[26] = t8;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const sidebarContent = t10;
    let t11;
    if ($[29] !== finalRailClassName || $[30] !== frameClassName || $[31] !== rail || $[32] !== sidebarContent) {
      t11 = rail ? <div className={cn("grid items-start gap-0 lg:grid-cols-[minmax(0,1fr)_auto]", frameClassName)}><div className="min-w-0 flex-1">{sidebarContent}</div><div className={finalRailClassName}>{rail}</div></div> : <div className={frameClassName}>{sidebarContent}</div>;
      $[29] = finalRailClassName;
      $[30] = frameClassName;
      $[31] = rail;
      $[32] = sidebarContent;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== backgroundClassName || $[35] !== t11) {
      t12 = <div className={backgroundClassName}>{t11}</div>;
      $[34] = backgroundClassName;
      $[35] = t11;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    return t12;
  }
  let t5;
  if ($[37] !== header) {
    t5 = header && <PageHeader {...header} />;
    $[37] = header;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  let t6;
  if ($[39] !== children || $[40] !== contentPadding) {
    t6 = <div className={contentPadding}>{children}</div>;
    $[39] = children;
    $[40] = contentPadding;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <StatusBadge />;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  let t9;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link href="https://docs.b3.fun"><span className="label label-xs hover:text-b3-blue transition-all">Docs</span></Link>;
    t9 = <MetaDot />;
    $[43] = t8;
    $[44] = t9;
  } else {
    t8 = $[43];
    t9 = $[44];
  }
  let t10;
  let t11;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Link href="/terms"><span className="label label-xs hover:text-b3-blue transition-all">Terms</span></Link>;
    t11 = <MetaDot />;
    $[45] = t10;
    $[46] = t11;
  } else {
    t10 = $[45];
    t11 = $[46];
  }
  let t12;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="border-ds-border/50 flex justify-between border-t px-6 py-3">{t7}<div className="flex items-center gap-4">{t8}{t9}{t10}{t11}<Link href="/privacy"><span className="label label-xs hover:text-b3-blue transition-all">Privacy</span></Link></div></div>;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== finalCardClassName || $[49] !== t5 || $[50] !== t6) {
    t13 = <div className={finalCardClassName}>{t5}{t6}{t12}</div>;
    $[48] = finalCardClassName;
    $[49] = t5;
    $[50] = t6;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  const pageCard = t13;
  let t14;
  if ($[52] !== finalRailClassName || $[53] !== frameClassName || $[54] !== pageCard || $[55] !== rail) {
    t14 = rail ? <div className={cn("m-2 grid items-start gap-0 lg:grid-cols-[minmax(0,1fr)_auto]", frameClassName)}><div className="min-w-0 flex-1">{pageCard}</div><div className={finalRailClassName}>{rail}</div></div> : <div className={cn("m-2 mr-11 mt-3", frameClassName)}>{pageCard}</div>;
    $[52] = finalRailClassName;
    $[53] = frameClassName;
    $[54] = pageCard;
    $[55] = rail;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== backgroundClassName || $[58] !== t14) {
    t15 = <div className={backgroundClassName}>{t14}</div>;
    $[57] = backgroundClassName;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
}